/**
 * What the sign-in screen may know about a shop before anybody is signed in.
 *
 * A device that has signed in before remembers the shop's look and name in the session hint. On
 * return, the sign-in screen wears that look — a Diamond till shows a dark cyan sign-in, not a
 * cream one with the wrong logo — so the branding is stable across the one moment the app would
 * otherwise have to guess.
 *
 * **Display only.** This reads a name and a theme code from `localStorage` and applies them to
 * `<html>`. It is not consulted by anything that authenticates, it is never sent to the server,
 * and a forged value gets somebody a cyan login form and nothing else.
 *
 * The address is consulted first. The dashboard is deployed one subdomain per shop, so
 * `diamond.example.com` opens Diamond's look even on a device that has never signed in — see
 * `shopThemeFromHost`. The POS is one till at `pos.example.com` for every shop, `pos` matches no
 * theme code, and its sign-in therefore stays neutral by construction.
 *
 * A device with no address hint and no session hint gets the neutral state: the neutral look, no
 * scheme opinion, and no shop name or mark. That is what every new device sees first and it is
 * designed as such, not left over.
 */

import { useEffect, useMemo } from 'react';

import { shopThemeFromHost } from '../theme/hostTheme.js';
import { applyShopTheme, clearShopTheme } from '../theme/shopTheme.js';
import { readHint } from './sessionSlice.js';

export type RememberedShop = {
  /**
   * Null when the address named the look but nobody has signed in on this device yet — we know
   * whose dashboard this is, not what is written over the door. Callers fall back to the product
   * name.
   */
  readonly name: string | null;
  readonly themeCode: string;
};

export function useRememberedShop(): RememberedShop | null {
  const remembered = useMemo((): RememberedShop | null => {
    const hint = readHint();
    const hinted =
      hint?.shopThemeCode !== undefined && hint.shopName !== undefined
        ? { name: hint.shopName, themeCode: hint.shopThemeCode }
        : null;

    const host = shopThemeFromHost(window.location.hostname);

    if (host === null) {
      return hinted;
    }

    // The address wins. On the dashboard it *is* the shop — one subdomain per shop — and it is
    // right on a device that has never signed in, which the hint cannot be. The hint supplies the
    // name only where the two agree, so a stale hint from another shop never puts its name over
    // this one's door.
    const agrees = hinted !== null && hinted.themeCode === host;

    return { name: agrees ? hinted.name : null, themeCode: host };
  }, []);

  useEffect(() => {
    if (remembered === null) clearShopTheme();
    else applyShopTheme(remembered.themeCode);
  }, [remembered]);

  return remembered;
}
